/*
 * F2 : indexation unique du snapshot. LIRE AVANT D'ÉCRIRE UNE PAGE (lots 3, 4, 5).
 *
 * Mémoïsé (jamais recalculé tant que les références d'entrée ne changent pas) :
 *   - indexer(snapshot, {collecte, rayon})         : par (identité du snapshot, collecte, rayon) (WeakMap) ; ~30 ms sur 70 000 événements.
 *     `collecte` (défaut false) : bouton « Collecte » (lot 8). À false, les événements `source === 'simulation'`
 *     (COLLECTE, COLLECTE_IMPOSSIBLE) sont exclus de l'index : les écrans sont identiques à ceux d'avant le lot 8.
 *     `index.collecte` porte le choix ; `bornes` ignore toujours les événements de collecte.
 *     `rayon` (défaut 150 m ; 100 / 150 / 250 / 400, lots 12 et 13) : chaque PAV indexé reçoit (`null` sans coordonnées) `populationAllouee`
 *     (Σ population des carreaux INSEE 200 m dont CETTE borne active est la plus proche DE SON GROUPE (`groupe` du patrimoine : extra / intra), à moins de `rayon` : pas de double compte dans un groupe),
 *     `populationAPortee` (Σ dans le rayon, avec double compte), `nbBornesAPortee` (autres bornes actives dans le rayon) et
 *     `partAllouee` (allouée / à portée) ; `index.rayon`, `index.carreaux` (lus dans `snapshot.territoire.carreaux`, chacun porte son `index`), `index.chalandise` et `index.nonRattaches` (Set d'index D9, vide si absent)
 *     (`snapshot.chalandise` : distances à pied OSRM, ou `{source: 'vol-oiseau'}`, lot 13) portent le choix. Options `carreaux` et `chalandise` : jeux de test.
 *     `populationAllouee150` : même allocation à rayon FIXE 150 m (grille rurale, lot 13 F), indépendante du curseur ; mémoïsée par (snapshot, rayon, carreaux, chalandise).
 *   - etatsDesPav(index, {acte, du, au, at})       : cache LRU de 8 jeux de paramètres par index (etat.js).
 *   - evenementsRetenus(index, pavs, {du, au, at}) : même LRU, par (index, référence du tableau `pavs`, paramètres).
 *   - rseRetenue(index, pavs, params)              : idem, via evenementsRetenus (rse.js).
 *
 * NON mémoïsé (bon marché, 0 à 20 ms sur le parc réel) mais à mettre dans un `useMemo` côté page :
 *   - filtrerPavs, kpi, repartitions, alertes, toCsv, toGeoJson.
 *   Règle : `useMemo(() => filtrerPavs(index, etats, filtres), [index, etats, filtres])`. Les sélecteurs mémoïsés
 *   ci-dessus se basent sur l'identité du tableau `pavs`, qui doit donc rester stable d'un rendu à l'autre
 *   (sinon le cache ne sert à rien). `filtres` et la plage doivent eux-mêmes être stables (useMemo / état).
 *
 * Typologies de maintenance : `index.typologiesMaintenanceParPatrimoine` (Map<patrimoineId, Set>) = `typologiesMaintenance`
 * exporté par l'API sur chaque patrimoine (PAT-04 R2 ; absent = vide). En acte `maint`, un IMPOSSIBLE n'est candidat que si
 * sa `detail.typologie` est dans le set du patrimoine de l'événement (`evenement.patrimoineId`) (B4).
 *
 * Communes : chaque PAV indexé reçoit `communeCle` (clé normalisée) et `communeLibelle` (graphie brute la plus
 * fréquente pour la clé) ; `commune` brute reste disponible. Le filtre `commune` du permalien est une CLÉ.
 */

import {couleurRang} from './circuits.js';
import {RAYON_DEFAUT, RAYON_SEUIL_RURAL, allouerPopulation, avecIndex, ensembleNonRattaches} from './population.js';
import {cleCommune} from './communeCle.js';

export {cleCommune};

const cache = new WeakMap();
const cachePopulations = new WeakMap();

export function comparerEvenements(a, b) {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    const ha = a.heure || '';
    const hb = b.heure || '';
    if (ha !== hb) return ha < hb ? -1 : 1;
    return 0;
}

function indexerCommunes(pavsBruts) {
    const graphies = new Map(); // cle -> Map<graphie brute, nb>
    for (const p of pavsBruts) {
        const cle = cleCommune(p.commune);
        if (cle === null) continue;
        let m = graphies.get(cle);
        if (!m) graphies.set(cle, (m = new Map()));
        m.set(p.commune, (m.get(p.commune) || 0) + 1);
    }
    const communes = [];
    for (const [cle, m] of graphies) {
        const choisie = [...m.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))[0][0];
        communes.push({cle, libelle: choisie, nbPav: [...m.values()].reduce((a, b) => a + b, 0)});
    }
    communes.sort((a, b) => a.libelle.localeCompare(b.libelle));
    return communes;
}

function couleursPatrimoines(patrimoinesBruts) {
    const rangs = new Map([...patrimoinesBruts].sort((a, b) => a.id - b.id).map((p, i) => [p.id, i]));
    return patrimoinesBruts.map((p) => ({...p, couleur: couleurRang(rangs.get(p.id)), couleurClair: couleurRang(rangs.get(p.id), {clair: true})}));
}

export const EST_SIMULE = (e) => e.source === 'simulation';

// Population allouée PAR GROUPE de patrimoine (extra / intra) : un carreau est alloué à la borne la plus proche de chaque groupe.
// Mémoïsé par (snapshot, rayon, carreaux, chalandise) : le curseur à 150 m ne double pas le calcul de la grille rurale.
function populationsPour(snapshot, groupeDuPav, rayon, carreaux, chalandise) {
    let parSnapshot = cachePopulations.get(snapshot);
    if (!parSnapshot) cachePopulations.set(snapshot, (parSnapshot = {}));
    const cle = `${rayon}|${idDe(carreaux)}|${idDe(chalandise)}`;
    if (parSnapshot[cle]) return parSnapshot[cle];
    const parGroupe = new Map();
    for (const p of snapshot.pav || []) {
        const g = groupeDuPav(p);
        if (!parGroupe.has(g)) parGroupe.set(g, []);
        parGroupe.get(g).push(p);
    }
    const populations = new Map();
    for (const liste of parGroupe.values()) for (const [id, v] of allouerPopulation(liste, carreaux, rayon, chalandise)) populations.set(id, v);
    return (parSnapshot[cle] = populations);
}

function construire(snapshot, collecte, rayon, carreaux, chalandise) {
    const pavsBruts = snapshot.pav || [];
    const communes = indexerCommunes(pavsBruts);
    const communeParCle = new Map(communes.map((c) => [c.cle, c]));
    // Copies : le snapshot RTK Query est figé, on ne l'enrichit pas en place.
    const patrimoines = couleursPatrimoines((snapshot.territoire && snapshot.territoire.patrimoines) || []);
    const groupeDe = new Map(patrimoines.map((q) => [q.id, q.groupe]));
    const groupeDuPav = (p) => groupeDe.get(p.patrimoineId) ?? null;
    const populations = populationsPour(snapshot, groupeDuPav, rayon, carreaux, chalandise);
    // Grille rurale (lot 13 F) : allocation à rayon fixe 150 m, indépendante du curseur ; réutilise le cache quand rayon === 150.
    const populations150 = rayon === RAYON_SEUIL_RURAL ? populations : populationsPour(snapshot, groupeDuPav, RAYON_SEUIL_RURAL, carreaux, chalandise);
    const pavs = pavsBruts.map((p) => {
        const cle = cleCommune(p.commune);
        return {...p, groupe: groupeDuPav(p), communeCle: cle, communeLibelle: cle === null ? null : communeParCle.get(cle).libelle, ...populations.get(p.id), populationAllouee150: populations150.get(p.id)?.populationAllouee ?? null};
    });
    const pavById = new Map(pavs.map((p) => [p.id, p]));
    const patrimoineById = new Map(patrimoines.map((p) => [p.id, p]));

    // Array.prototype.sort est stable : à date/heure égales, l'ordre du snapshot est conservé.
    const evenements = (snapshot.evenements || []).filter((e) => collecte || !EST_SIMULE(e)).sort(comparerEvenements);

    const evenementsParPav = new Map();
    const elements = new Set(); // libellés d'éléments de checklist connus
    let min = null;
    let max = null;
    for (const e of evenements) {
        let liste = evenementsParPav.get(e.pavId);
        if (!liste) {
            liste = [];
            evenementsParPav.set(e.pavId, liste);
        }
        liste.push(e);
        if (e.type !== 'PLANIFIÉ' && e.type !== 'COLLECTE' && e.type !== 'COLLECTE_IMPOSSIBLE') {
            if (min === null || e.date < min) min = e.date;
            if (max === null || e.date > max) max = e.date;
        }
        if (e.type === 'MAINT_PREV') {
            for (const el of e.elements || []) elements.add(el.libelle);
        }
    }

    // Typologies de maintenance (PAT-04 R2) : par patrimoine, exportées par l'API ; absence = liste vide.
    const typologiesMaintenanceParPatrimoine = new Map(patrimoines.map((p) => [p.id, new Set(p.typologiesMaintenance ?? [])]));

    return {collecte, rayon, carreaux, chalandise, nonRattaches: ensembleNonRattaches(chalandise), typologiesMaintenanceParPatrimoine, pavs, pavById, patrimoineById, evenementsParPav, evenements, bornes: {min, max}, communes, communeParCle, elements, snapshot};
}

const idObjets = new WeakMap();
let prochainId = 0;
const idDe = (o) => {
    if (o === null) return 'n';
    if (!idObjets.has(o)) idObjets.set(o, prochainId++);
    return idObjets.get(o);
};
const AUCUN_CARREAU = [];

export function indexer(snapshot, {collecte = false, rayon = RAYON_DEFAUT, carreaux, chalandise} = {}) {
    if (!snapshot) return null;
    carreaux = avecIndex(carreaux ?? snapshot.territoire?.carreaux ?? AUCUN_CARREAU);
    chalandise = chalandise !== undefined ? chalandise : (snapshot.chalandise ?? null);
    let parSnapshot = cache.get(snapshot);
    if (!parSnapshot) {
        parSnapshot = {};
        cache.set(snapshot, parSnapshot);
    }
    const cle = `${collecte ? 'avec' : 'sans'}|${rayon}|${idDe(carreaux)}|${idDe(chalandise)}`;
    if (!parSnapshot[cle]) parSnapshot[cle] = construire(snapshot, !!collecte, rayon, carreaux, chalandise);
    return parSnapshot[cle];
}
